import React, { useState, useCallback, useRef } from 'react';
import {
  ScorecardWidgetInstanceConfig,
  ScorecardDataContent,
  ScoreValue,
} from './types';
import { WidgetEmptyState } from '../../../design';
import { SCORECARD_LAYOUT_COMPONENTS } from './layouts';

interface ScorecardWidgetRendererProps {
  config: ScorecardWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: ScorecardDataContent) => void;
  readOnly?: boolean;
}

const ScorecardWidgetRenderer: React.FC<ScorecardWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const data = dashboardData[config.dataKey] as ScorecardDataContent | undefined;

  // No auto-default: an unanswered required scorecard must read as
  // *unselected* (null), not silently pinned to neutral '=' which would
  // make it look answered and defeat the completion gate.
  const [localScore, setLocalScore] = useState<ScoreValue | null>(data?.score ?? null);
  const lastPropScore = useRef(data?.score);

  // Sync from props only when the prop actually changes (server confirmed)
  if (data?.score !== lastPropScore.current) {
    lastPropScore.current = data?.score;
    setLocalScore(data?.score ?? null);
  }

  const handleSelect = useCallback(
    (value: ScoreValue) => {
      if (readOnly || !onDataChange) return;
      setLocalScore(value);
      onDataChange(config.dataKey, { score: value });
    },
    [readOnly, onDataChange, config.dataKey],
  );

  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  // Pick a layout. Falls back to carousel if the saved variant is unknown
  // (e.g. an older config from before a layout was renamed).
  const LayoutComponent =
    SCORECARD_LAYOUT_COMPONENTS[config.layout ?? 'carousel'] ??
    SCORECARD_LAYOUT_COMPONENTS.carousel;

  const showRequired = !localScore && !readOnly;
  // Read-only viewers (e.g. a manager reviewing a workup) can't see the
  // "Selection required" editor cue, so an unrated card would read as an
  // ambiguous muted bar — indistinguishable from a real neutral '=' grade.
  // Surface an explicit "Not rated yet" so unscored steps are unmistakable.
  const showNotRated = !localScore && readOnly;

  return (
    <div className="relative h-full">
      <LayoutComponent
        currentScore={localScore}
        onSelect={handleSelect}
        readOnly={readOnly}
      />
      {showRequired && (
        <p className="absolute bottom-1 inset-x-0 text-center text-xs text-danger-500 font-medium pointer-events-none">
          Selection required
        </p>
      )}
      {showNotRated && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface/70 pointer-events-none">
          <span className="px-2 py-0.5 text-xs font-medium rounded-full border border-dashed border-neutral-300 text-neutral-400 bg-surface">
            Not rated yet
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(ScorecardWidgetRenderer);
